/* eslint-disable import/no-extraneous-dependencies */
import { useMemo, useRef } from "react";

import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import ReactPlayer from "react-player";

import { EntityMediaType } from "../../hooks/useGetMedia";

type Props = {
  url: string;
  type: EntityMediaType;
  fileName?: string;
  thumbnailUrl?: string;
  className?: string;
  cookies?: Array<{
    name: string;
    value: string;
    path?: string;
    domain?: string;
    maxAge?: number;
    secure?: boolean;
  }>;
  onDelete?: () => void;
};

export function MediaPreview({
  url,
  type,
  fileName,
  thumbnailUrl,
  className = "",
  cookies,
  onDelete,
}: Props) {
  const { t } = useTranslation();
  const videoRef = useRef<unknown>(null);
  const audioRef = useRef<unknown>(null);

  // Prepare cookies for HLS requests - backend will forward them to CloudFront
  const cookieHeader = useMemo(() => {
    if (!cookies || cookies.length === 0) return "";
    return cookies.map((c) => `${c.name}=${c.value}`).join("; ");
  }, [cookies]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Player = ReactPlayer as any;

  const mediaUrl = useMemo(() => {
    try {
      // Check if URL is from CloudFront (any cloudfront.net domain)
      const urlObj = new URL(url);
      if (urlObj.hostname.includes("cloudfront.net")) {
        // Use simple path format: /cfproxy/path (CLOUDFRONT_URI env var handles the domain)
        return `/cfproxy${urlObj.pathname}${urlObj.search}`;
      }
    } catch {
      /* swallow invalid URL */
    }
    return url;
  }, [url]);

  return (
    <div
      className={`mt-4 p-4 border border-gray-300 rounded-lg bg-gray-50 relative ${className}`}
    >
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="absolute top-2 right-2 rounded-full text-red-600 hover:text-red-800 transition-colors"
          aria-label={t("app.media.delete")}
        >
          <FontAwesomeIcon icon={faTrash} />
        </button>
      )}
      <div className="mb-2 text-center">
        <span className="text-sm font-semibold text-gray-700">
          {t("app.media.uploaded")}:
        </span>
        {fileName && (
          <span className="ml-2 text-sm text-gray-600">
            {fileName.length > 20
              ? `${fileName.substring(0, 20)}...`
              : fileName}
          </span>
        )}
        <span className="ml-2 text-xs text-gray-500 uppercase">({type})</span>
      </div>

      <div className="mt-3 flex justify-center">
        {type === "image" && (
          <img
            src={url}
            alt={fileName || "Uploaded image"}
            className="max-w-full max-h-64 rounded-md shadow-sm"
          />
        )}

        {type === "video" && (
          <div className="max-w-full flex flex-col items-center">
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <Player
              ref={videoRef}
              src={mediaUrl}
              controls
              config={{
                hls: {
                  xhrSetup: (xhr: XMLHttpRequest, requestUrl: string) => {
                    if (cookieHeader) {
                      // Use custom header for proxied requests (same-origin)
                      // Browser will block Cookie header for same-origin requests
                      xhr.setRequestHeader("X-CloudFront-Cookie", cookieHeader);
                    }
                  },
                  // },
                },
              }}
              style={{ width: "100%", aspectRatio: "16/9" }}
              onError={(e: unknown) => {
                // eslint-disable-next-line no-console
                console.error("Video playback error", e);
              }}
            />
          </div>
        )}

        {type === "audio" && (
          <div className="w-full flex flex-col items-center">
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <Player
              ref={audioRef}
              src={mediaUrl}
              controls
              config={{
                hls: {
                  xhrSetup: (xhr: XMLHttpRequest, requestUrl: string) => {
                    if (cookieHeader) {
                      // Use custom header for proxied requests (same-origin)
                      // Browser will block Cookie header for same-origin requests
                      xhr.setRequestHeader("X-CloudFront-Cookie", cookieHeader);
                    }
                  },
                  // },
                },
              }}
              width="100%"
              height="50px"
              style={{ pointerEvents: "auto" }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
